import { Box, Grid, MultiSelect, Switch, Text } from "@dehub/theme";
import type { UseFormReturnType } from "@dehub/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { renderBusinessName } from "@/utils/render-business-name";
import classes from "../../warehouse.module.css";
import type { IBranchRow, IFormData } from "./warehouse-form";

type Props = {
  form: UseFormReturnType<IFormData>;
  branches?: { rows?: IBranchRow[] };
  isDetail?: boolean;
};

/** "Receives goods" / "Ships goods": switches like the branch form (the form values stay "true" / "false"). */
export function WarehouseBuyerSupplierConfig({ form, branches, isDetail }: Props) {
  const { t } = useTranslation("warehouse");

  const selectedBranch = useMemo(
    () => branches?.rows?.find((b) => b.id === form.values.branchId),
    [branches, form.values.branchId],
  );

  const toOptions = (list?: { id: string; refCode: string; profileName: string; profileNameEng?: string | null }[]) =>
    list?.map((b) => ({
      label: `${b.refCode} / ${renderBusinessName({ profileName: b.profileName, profileNameEng: b.profileNameEng })}`,
      value: b.id,
    })) ?? [];
  const buyerOptions = useMemo(() => toOptions(selectedBranch?.buyers), [selectedBranch]);
  const supplierOptions = useMemo(() => toOptions(selectedBranch?.suppliers), [selectedBranch]);

  const receives = form.values.isBuyer === "true" || form.values.isBuyer === true;
  const ships = form.values.isSupplier === "true" || form.values.isSupplier === true;

  return (
    <>
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <Box className={classes.switchCard}>
          <Text fz={14} fw={500}>{t("form.doReceive")}</Text>
          <Switch
            aria-label={t("form.doReceive")}
            disabled={isDetail}
            checked={receives}
            onChange={(event) => {
              form.setFieldValue("isBuyer", String(event.currentTarget.checked));
              form.setFieldValue("buyers", []);
            }}
          />
        </Box>
        {receives ? (
          <Box mt="sm">
            <MultiSelect data={buyerOptions} label={t("form.buyerBiz")} placeholder={t("form.buyerBiz")} {...form.getInputProps("buyers")} />
            <Text fz={14} c="dimmed" mt={2}>{t("form.branchOnly")}</Text>
          </Box>
        ) : null}
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <Box className={classes.switchCard}>
          <Text fz={14} fw={500}>{t("form.doShip")}</Text>
          <Switch
            aria-label={t("form.doShip")}
            disabled={isDetail}
            checked={ships}
            onChange={(event) => {
              form.setFieldValue("isSupplier", String(event.currentTarget.checked));
              form.setFieldValue("suppliers", []);
            }}
          />
        </Box>
        {ships ? (
          <Box mt="sm">
            <MultiSelect data={supplierOptions} label={t("form.supplierBiz")} placeholder={t("form.supplierBiz")} {...form.getInputProps("suppliers")} />
            <Text fz={14} c="dimmed" mt={2}>{t("form.branchOnly")}</Text>
          </Box>
        ) : null}
      </Grid.Col>
    </>
  );
}
